// Streamer-Unterseite: /community/:slug
// Standalone-Seite (nicht über Dashboard-Tab-System).

import { api } from '../../services/api.js';
import { navigate, getRouteParams } from '../../router.js';
import { gameState } from '../../state/gameState.js';
import { DESKTOP_LAYOUT_QUERY } from '../../utils/mobile-layout.js';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TickerEntry {
  type: string;
  description: string;
  timestamp: string;
}

interface VotingOption {
  eventType: string;
  label: string;
  description: string;
  votes: number;
  hasVoted: boolean;
}

interface ChallengeData {
  id: string;
  type: string;
  description: string;
  target: number;
  progress: number;
  rewardStreampoints: number;
  rewardItemDrop: boolean;
  rewardMoneyCents: number;
  rewardBlackMoney: number;
  isCompleted: boolean;
  rewardClaimed: boolean;
  isLiveChallenge: boolean;
}

interface ActiveEvent {
  eventType: string;
  label: string;
  description: string;
  endsAt: string;
}

interface VotingState {
  eventId: string;
  options: VotingOption[];
  closesAt: string;
  hasVoted: boolean;
}

interface StreamerPageData {
  id: string;
  displayName: string;
  twitchUsername: string;
  slug: string;
  isLive: boolean;
  lastLiveAt: string | null;
  activeEvent: ActiveEvent | null;
  voting: VotingState | null;
  challenge: ChallengeData | null;
  liveTicker: TickerEntry[];
  hasCommunityPass: boolean;
  streampoints: number;
}

// ─── State ────────────────────────────────────────────────────────────────────

let countdownInterval: ReturnType<typeof setInterval> | null = null;
let tickerInterval: ReturnType<typeof setInterval> | null = null;
let currentSlug = '';
let pageData: StreamerPageData | null = null;

// ─── Render ───────────────────────────────────────────────────────────────────

export async function renderStreamerPage(): Promise<void> {
  const params = getRouteParams();
  const slug = params.slug ?? '';

  if (!slug) {
    navigate('/community/stream');
    return;
  }

  currentSlug = slug;
  cleanup();

  const app = document.getElementById('app');
  if (!app) return;

  // Check auth
  const player = gameState.getPlayer();
  if (!player) {
    navigate('/login');
    return;
  }

  app.innerHTML = renderShell();
  applyStyles();
  // Zurück-Button sofort binden — auch der Fehlerzustand muss ihn nutzen können.
  app.querySelector('#btn-streamer-back')?.addEventListener('click', () => {
    navigate('/community/stream');
  });
  // Zurück-Button sofort binden — auch der Fehlerzustand muss ihn nutzen können.
  try {
    pageData = await api.get<StreamerPageData>(`/community/${slug}`);
    renderContent(app, pageData);
    startCountdowns(pageData);
    startTickerRefresh();
  } catch {
    const content = document.getElementById('streamer-content');
    if (content) content.innerHTML = '<div class="streamer-error">STREAMER NICHT GEFUNDEN.</div>';
  }
}

function renderShell(): string {
  return `
    <div class="streamer-page" id="streamer-root">
      <div class="streamer-back-bar">
        <button class="btn-streamer-back" id="btn-streamer-back">← ZURÜCK</button>
      </div>
      <main class="streamer-main" id="streamer-content">
        <div class="streamer-loading">LADE STREAMER-DATEN...</div>
      </main>
    </div>
  `;
}

function renderContent(app: HTMLElement, data: StreamerPageData): void {
  const content = document.getElementById('streamer-content');
  if (!content) return;

  content.innerHTML = `
    ${renderStreamerHeader(data)}
    <div class="streamer-body">
      <div class="streamer-col-left">
        ${data.activeEvent ? renderActiveEvent(data.activeEvent) : ''}
        ${data.challenge ? renderChallenge(data.challenge) : ''}
        ${renderVoting(data)}
      </div>
      <div class="streamer-col-right">
        ${data.isLive ? renderLiveTicker(data.liveTicker) : ''}
      </div>
    </div>
  `;

  attachListeners(app, data);
}

function renderStreamerHeader(data: StreamerPageData): string {
  const liveHtml = data.isLive
    ? '<span class="streamer-live-dot"></span><span class="streamer-live-text">LIVE</span>'
    : '<span class="streamer-offline-text">OFFLINE</span>';

  return `
    <div class="streamer-header-card">
      <div class="streamer-header-top">
        <span class="streamer-display-name">${data.displayName.toUpperCase()}</span>
        <span class="streamer-live-status">${liveHtml}</span>
      </div>
      <div class="streamer-header-bottom">
        <span class="streamer-twitch-url">twitch.tv/${data.twitchUsername}</span>
        ${data.isLive ? `<a href="https://twitch.tv/${data.twitchUsername}" target="_blank" rel="noopener noreferrer" class="btn-streamer-watch">JETZT ZUSCHAUEN</a>` : ''}
      </div>
      ${data.streampoints > 0 ? `<div class="streamer-points">DEINE STREAMPOINTS: ${String(data.streampoints)}</div>` : ''}
    </div>
  `;
}

function renderActiveEvent(event: ActiveEvent): string {
  return `
    <div class="streamer-section" id="section-event">
      <div class="streamer-section-title">AKTIVES EVENT</div>
      <div class="streamer-event-name">${event.label.toUpperCase()}</div>
      <div class="streamer-event-desc">${event.description.toUpperCase()}</div>
      <div class="streamer-event-countdown">
        ENDET IN: <span id="event-countdown" data-ends-at="${event.endsAt}">--:--:--</span>
      </div>
    </div>
  `;
}

function renderChallenge(challenge: ChallengeData): string {
  const pct = challenge.target > 0
    ? Math.min(100, Math.round((challenge.progress / challenge.target) * 100))
    : 0;
  const filledBars = Math.round(pct / 10);
  const progressBar = '█'.repeat(filledBars) + '░'.repeat(10 - filledBars);

  const rewards: string[] = [];
  if (challenge.rewardStreampoints > 0) rewards.push(`${String(challenge.rewardStreampoints)} STREAMPOINTS`);
  if (challenge.rewardMoneyCents > 0) rewards.push(`${String(Math.round(challenge.rewardMoneyCents / 100))} ₦`);
  if (challenge.rewardBlackMoney > 0) rewards.push(`${String(Math.round(challenge.rewardBlackMoney / 100))} S-₦`);
  if (challenge.rewardItemDrop) rewards.push('ITEM-DROP');

  const claimable = challenge.isCompleted && !challenge.rewardClaimed;
  const claimBtnClass = claimable ? 'btn-challenge-claim' : 'btn-challenge-claim btn-challenge-claim--disabled';

  return `
    <div class="streamer-section" id="section-challenge">
      <div class="streamer-section-title">TAGES-CHALLENGE</div>
      <div class="streamer-challenge-desc">${challenge.description.toUpperCase()}</div>
      <div class="streamer-challenge-progress">
        <span class="challenge-bar">[${progressBar}]</span>
        <span class="challenge-numbers">${String(challenge.progress)} / ${String(challenge.target)}</span>
      </div>
      <div class="streamer-challenge-reward">BELOHNUNG: ${rewards.join(' + ')}</div>
      ${challenge.rewardClaimed
        ? '<div class="challenge-claimed">BELOHNUNG BEREITS EINGEFORDERT.</div>'
        : `<button class="${claimBtnClass}" id="btn-claim-challenge" data-challenge-id="${challenge.id}" ${claimable ? '' : 'disabled'}>REWARD EINFORDERN</button>`
      }
    </div>
  `;
}

function renderVoting(data: StreamerPageData): string {
  if (!data.hasCommunityPass) {
    return `
      <div class="streamer-section streamer-section--locked">
        <div class="streamer-section-title">COMMUNITY PASS</div>
        <div class="streamer-pass-locked">COMMUNITY PASS ERFORDERLICH.</div>
      </div>
    `;
  }

  if (!data.voting) {
    return `
      <div class="streamer-section">
        <div class="streamer-section-title">COMMUNITY PASS</div>
        <div class="streamer-no-voting">KEINE AKTIVE ABSTIMMUNG.</div>
      </div>
    `;
  }

  const voting = data.voting;
  const totalVotes = voting.options.reduce((s, o) => s + o.votes, 0);

  return `
    <div class="streamer-section" id="section-voting">
      <div class="streamer-section-title">COMMUNITY PASS – ABSTIMMUNG</div>
      <div class="streamer-voting-countdown">
        ENDET IN: <span id="voting-countdown" data-ends-at="${voting.closesAt}">--:--:--</span>
      </div>
      <div class="streamer-voting-options" id="voting-options">
        ${voting.options.map((opt) => renderVotingOption(opt, totalVotes, voting.hasVoted, voting.eventId)).join('')}
      </div>
      ${!voting.hasVoted ? `<button class="btn-vote" id="btn-submit-vote" data-event-id="${voting.eventId}">STIMME ABGEBEN</button>` : '<div class="vote-done">STIMME ABGEGEBEN.</div>'}
    </div>
  `;
}

function renderVotingOption(opt: VotingOption, total: number, hasVoted: boolean, eventId: string): string {
  const checked = opt.hasVoted ? 'checked' : '';
  const votedClass = opt.hasVoted ? ' voting-opt--voted' : '';
  const disabledAttr = hasVoted ? 'disabled' : '';

  return `
    <label class="voting-opt${votedClass}">
      <input type="radio" name="vote-${eventId}" value="${opt.eventType}" ${checked} ${disabledAttr}>
      <span class="voting-opt-label">${opt.label.toUpperCase()}</span>
      <span class="voting-opt-desc">${opt.description}</span>
      <span class="voting-opt-count">[${String(opt.votes)}${total > 0 ? ` / ${String(total)}` : ''}]</span>
    </label>
  `;
}

function renderLiveTicker(ticker: TickerEntry[]): string {
  if (ticker.length === 0) {
    return `
      <div class="streamer-section">
        <div class="streamer-section-title">LIVE AUS DER STADT</div>
        <div class="streamer-ticker-empty">NOCH KEINE EREIGNISSE.</div>
      </div>
    `;
  }

  const entries = ticker.slice(0, 10).map((e) => {
    const age = formatAge(new Date(e.timestamp));
    const colorClass = getTickerColorClass(e.type);
    return `
      <div class="ticker-entry ${colorClass}">
        <span class="ticker-age">${age}</span>
        <span class="ticker-desc">${e.description}</span>
      </div>
    `;
  }).join('');

  return `
    <div class="streamer-section streamer-section--ticker" id="section-ticker">
      <div class="streamer-section-title">LIVE AUS DER STADT</div>
      <div class="streamer-ticker-list">${entries}</div>
    </div>
  `;
}

// ─── Listeners ────────────────────────────────────────────────────────────────

function attachListeners(app: HTMLElement, _data: StreamerPageData): void {

  app.querySelector('#btn-claim-challenge')?.addEventListener('click', (e) => {
    void (async () => {
      const btn = e.currentTarget as HTMLButtonElement;
      const challengeId = btn.dataset.challengeId;
      if (!challengeId) return;
      btn.disabled = true;
      try {
        await api.post(`/community/${currentSlug}/challenge/claim`, { challengeId });
        pageData = await api.get<StreamerPageData>(`/community/${currentSlug}`);
        const content = document.getElementById('streamer-content');
        const appEl = document.getElementById('app'); if (content && appEl) renderContent(appEl, pageData);
      } catch {
        btn.disabled = false;
      }
    })();
  });

  app.querySelector('#btn-submit-vote')?.addEventListener('click', (e) => {
    void (async () => {
      const btn = e.currentTarget as HTMLButtonElement;
      const eventId = btn.dataset.eventId;
      if (!eventId) return;

      const selected = app.querySelector<HTMLInputElement>(`input[name="vote-${eventId}"]:checked`);
      if (!selected) return;

      btn.disabled = true;
      try {
        await api.post(`/community/${currentSlug}/vote`, {
          eventId,
          eventType: selected.value,
        });
        pageData = await api.get<StreamerPageData>(`/community/${currentSlug}`);
        const content = document.getElementById('streamer-content');
        const appEl = document.getElementById('app'); if (content && appEl) renderContent(appEl, pageData);
      } catch {
        btn.disabled = false;
      }
    })();
  });
}

// ─── Countdowns ───────────────────────────────────────────────────────────────

function startCountdowns(_data: StreamerPageData): void {
  if (countdownInterval !== null) clearInterval(countdownInterval);
  tickCountdowns();
  countdownInterval = setInterval(tickCountdowns, 1_000);
}

function tickCountdowns(): void {
  const now = Date.now();
  document.querySelectorAll<HTMLElement>('[data-ends-at]').forEach((el) => {
    const endsAt = new Date(el.dataset.endsAt ?? '').getTime();
    const diff = endsAt - now;
    if (diff <= 0) {
      el.textContent = '00:00:00';
      return;
    }
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1_000);
    el.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
  });
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// ─── Ticker-Refresh ───────────────────────────────────────────────────────────

function startTickerRefresh(): void {
  if (tickerInterval !== null) clearInterval(tickerInterval);
  tickerInterval = setInterval(() => void refreshTicker(), 30_000);
}

async function refreshTicker(): Promise<void> {
  try {
    const fresh = await api.get<StreamerPageData>(`/community/${currentSlug}`);
    const tickerSection = document.getElementById('section-ticker');
    if (tickerSection && fresh.isLive) {
      tickerSection.outerHTML = renderLiveTicker(fresh.liveTicker);
    }
  } catch {
    // still ignorieren
  }
}

// ─── Cleanup ─────────────────────────────────────────────────────────────────

function cleanup(): void {
  if (countdownInterval !== null) { clearInterval(countdownInterval); countdownInterval = null; }
  if (tickerInterval !== null) { clearInterval(tickerInterval); tickerInterval = null; }
  pageData = null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatAge(ts: Date): string {
  const ms = Date.now() - ts.getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'GERADE EBEN';
  if (mins === 1) return 'VOR 1 MIN';
  return `VOR ${String(mins)} MIN`;
}

function getTickerColorClass(type: string): string {
  if (type === 'ARREST' || type === 'RAID') return 'ticker-red';
  if (type === 'HARVEST' || type === 'EARN') return 'ticker-green';
  if (type === 'LAUNDRY' || type === 'MONEY') return 'ticker-gold';
  return 'ticker-muted';
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function applyStyles(): void {
  const id = 'streamer-page-styles';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    .streamer-page {
      min-height: 100vh;
      background: var(--bg-base);
      color: var(--text-primary);
      font-family: 'Share Tech Mono', 'Courier New', monospace;
    }

    .streamer-back-bar {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--border);
      background: var(--bg-surface);
    }

    .btn-streamer-back {
      background: none;
      border: 1px solid var(--border);
      color: var(--text-muted);
      font-family: inherit;
      font-size: var(--font-size-xs);
      cursor: pointer;
      padding: 0.25rem 0.75rem;
      border-radius: var(--radius-btn);
    }

    .btn-streamer-back:hover { color: var(--text-primary); border-color: var(--text-muted); }

    .streamer-main {
      max-width: 900px;
      margin: 0 auto;
      padding: 1rem;
    }

    .streamer-loading, .streamer-error {
      padding: 2rem;
      color: var(--text-muted);
      font-size: var(--font-size-xs);
      text-align: center;
    }

    .streamer-header-card {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .streamer-header-top {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.5rem;
    }

    .streamer-display-name {
      font-size: var(--font-size-xl);
      color: var(--text-primary);
    }

    .streamer-live-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--accent-green);
      display: inline-block;
      animation: pulse-dot 1.5s ease-in-out infinite;
    }

    @keyframes pulse-dot {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.3; }
    }

    .streamer-live-text { color: var(--accent-green); font-size: var(--font-size-xs); }
    .streamer-offline-text { color: var(--text-muted); font-size: var(--font-size-xs); }

    .streamer-header-bottom {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex-wrap: wrap;
    }

    .streamer-twitch-url { color: var(--text-muted); font-size: var(--font-size-xs); }

    .btn-streamer-watch {
      background: var(--accent-purple);
      color: var(--bg-base);
      border: none;
      border-radius: var(--radius-btn);
      padding: 0.25rem 0.75rem;
      font-family: inherit;
      font-size: var(--font-size-xs);
      cursor: pointer;
      text-decoration: none;
      display: inline-block;
    }

    .btn-streamer-watch:hover { opacity: 0.85; }

    .streamer-points {
      margin-top: 0.5rem;
      font-size: var(--font-size-xs);
      color: var(--accent-gold);
    }

    .streamer-body {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
    }

    @media ${DESKTOP_LAYOUT_QUERY} {
      .streamer-body {
        grid-template-columns: 1fr 1fr;
      }
    }

    .streamer-col-left, .streamer-col-right {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .streamer-section {
      background: var(--bg-surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      padding: 1rem;
    }

    .streamer-section--locked {
      opacity: 0.6;
    }

    .streamer-section-title {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      border-bottom: 1px solid var(--border);
      padding-bottom: 0.5rem;
      margin-bottom: 0.75rem;
    }

    .streamer-event-name {
      font-size: var(--font-size-base);
      color: var(--accent-gold);
      margin-bottom: 0.25rem;
    }

    .streamer-event-desc {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-bottom: 0.5rem;
    }

    .streamer-event-countdown {
      font-size: var(--font-size-xs);
      color: var(--text-primary);
    }

    #event-countdown, #voting-countdown {
      color: var(--accent-green);
    }

    .streamer-challenge-desc {
      font-size: var(--font-size-sm);
      color: var(--text-primary);
      margin-bottom: 0.75rem;
    }

    .streamer-challenge-progress {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.5rem;
    }

    .challenge-bar {
      font-size: var(--font-size-xs);
      color: var(--accent-green);
    }

    .challenge-numbers {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
    }

    .streamer-challenge-reward {
      font-size: var(--font-size-xs);
      color: var(--accent-gold);
      margin-bottom: 0.75rem;
    }

    .btn-challenge-claim {
      background: var(--accent-green);
      color: var(--bg-base);
      border: none;
      border-radius: var(--radius-btn);
      padding: 0.35rem 0.85rem;
      font-family: inherit;
      font-size: var(--font-size-xs);
      cursor: pointer;
    }

    .btn-challenge-claim--disabled,
    .btn-challenge-claim:disabled {
      background: var(--bg-elevated);
      color: var(--text-muted);
      cursor: not-allowed;
    }

    .challenge-claimed {
      font-size: var(--font-size-xs);
      color: var(--accent-green);
    }

    .streamer-pass-locked, .streamer-no-voting {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
    }

    .streamer-voting-countdown {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
      margin-bottom: 0.75rem;
    }

    .voting-opt {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.5rem 0;
      border-bottom: 1px solid var(--border);
      cursor: pointer;
      font-size: var(--font-size-xs);
    }

    .voting-opt--voted { color: var(--accent-gold); }
    .voting-opt input { margin-top: 2px; accent-color: var(--accent-gold); }

    .voting-opt-label { flex: 1; color: var(--text-primary); }
    .voting-opt-desc { color: var(--text-muted); font-size: 0.8125rem; }
    .voting-opt-count { color: var(--accent-gold); white-space: nowrap; }

    .btn-vote {
      margin-top: 0.75rem;
      background: var(--accent-gold);
      color: var(--bg-base);
      border: none;
      border-radius: var(--radius-btn);
      padding: 0.35rem 0.85rem;
      font-family: inherit;
      font-size: var(--font-size-xs);
      cursor: pointer;
    }

    .btn-vote:disabled { opacity: 0.5; cursor: not-allowed; }
    .vote-done { margin-top: 0.5rem; font-size: var(--font-size-xs); color: var(--accent-green); }

    .streamer-section--ticker { overflow: hidden; }

    .streamer-ticker-list {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      max-height: 320px;
      overflow-y: auto;
    }

    .ticker-entry {
      display: flex;
      gap: 0.75rem;
      font-size: var(--font-size-xs);
    }

    .ticker-age { color: var(--text-muted); white-space: nowrap; min-width: 80px; }
    .ticker-desc { color: var(--text-primary); }
    .ticker-red .ticker-desc { color: var(--accent-red); }
    .ticker-green .ticker-desc { color: var(--accent-green); }
    .ticker-gold .ticker-desc { color: var(--accent-gold); }
    .ticker-muted .ticker-desc { color: var(--text-muted); }

    .streamer-ticker-empty {
      font-size: var(--font-size-xs);
      color: var(--text-muted);
    }
  `;
  document.head.appendChild(style);
}
